import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, TextField } from '@nyce/admin-kit/ui';
import { Page, useAsync, useInterval } from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';
import { TenantPicker } from '../../identity/TenantPicker';
import { activeTarget, authClientFor, useTargets } from '../../identity/session';
import { printQueue, type TenantAuth } from '../../printqueue/api/client';
import { SCOPES } from '../../api';
import { formatRelative } from '../../lib/format';
import { targetOf, useOnboardingConfig } from '../api/config';
import { addAlias, addScopes, aliasOwner, clientWith, missingScopes } from '../api/auth';
import { printServerFiles } from '../generate/printserver';
import { workServerFiles } from '../generate/workserver';
import { guidePath } from '../guides';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { PLACEHOLDER, rememberSecret, useSecrets } from '../wizard/secrets';
import { Facts, NeedsScope, NeedsSession, Note, StepActions, useAction } from '../wizard/parts';

const SCOPES_PQ = ['printqueue:enqueue', 'printqueue:print'];
const GUID = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

/**
 * `auth`: the AUTH tenant is the print queue tenant (its id; print queue 1.4.0+, WorkServer and print servers with
 * AUTH clients only, no tokens, no alias). `name`: the WorkServer's organization name, linked by the alias
 * `printqueue:<TenantId>`, with print queue tokens as the fallback (installations from before AUTH).
 */
type Setup = 'auth' | 'name';

/** W6: a print queue tenant, its WorkServer, and print servers with their config file, installer and AUTH. */
export function PrintQueueWizardPage() {
  return (
    <Page
      title="Print queue & print servers"
      description="Set up a customer's printing: the print queue tenant, its link to AUTH, the WorkServer's settings, and each print server with its config file and installer. Steps already in place show as done."
    >
      <TargetShell service="printqueue">
        <PrintQueueWizard />
      </TargetShell>
    </Page>
  );
}

function PrintQueueWizard() {
  const target = activeTarget();
  const pq = useMemo(() => printQueue(target), [target]);
  const config = useOnboardingConfig();
  const env = targetOf(config, target);
  const { signedIn } = useTargets();
  const session = signedIn(target);
  const secrets = useSecrets();
  const [tenantId, setTenantId] = useWizardParam('tenant');
  const [authTenant, setAuthTenant] = useWizardParam('authTenant');
  const [server, setServer] = useWizardParam('server');
  const [platform, setPlatform] = useWizardParam('platform');
  const [step, setStep] = useWizardParam('step');
  const [setupParam, setSetup] = useWizardParam('setup');
  const plat = platform === 'cups' ? 'cups' : 'windows';
  const setup: Setup =
    setupParam === 'auth' || setupParam === 'name'
      ? setupParam
      : !tenantId || GUID.test(tenantId)
        ? 'auth'
        : 'name';
  const authOnly = setup === 'auth';
  // AUTH only: an AUTH tenant handed over (customer checklist) is the print queue tenant
  useEffect(() => {
    if (authOnly && !tenantId && authTenant) setTenantId(authTenant);
  }, [authOnly, tenantId, authTenant, setTenantId]);

  const state = useAsync(async () => {
    const [modes, tokens, printers] = await Promise.all([
      pq.tenantAuth().catch(() => [] as TenantAuth[]),
      tenantId ? pq.tokens(tenantId) : Promise.resolve([]),
      tenantId ? pq.printers({ tenantId }) : Promise.resolve([]),
    ]);
    let owner = null;
    let claims = null;
    let clients = null;
    let tenantName: string | null = null;
    if (session && tenantId) {
      const c = authClientFor(target);
      // AUTH only: the TenantId is the AUTH tenant itself, no alias to look up
      owner = authOnly ? null : await aliasOwner(target, 'printqueue', tenantId);
      const tid = authOnly ? tenantId : (owner?.tenantId ?? authTenant);
      if (tid) {
        const [cl, cs, t] = await Promise.all([
          c.tenants.claims.list(tid),
          c.tenants.clients.list(tid),
          authOnly ? c.tenants.get(tid).catch(() => null) : Promise.resolve(null),
        ]);
        claims = cl;
        clients = cs;
        tenantName = t?.name ?? null;
      }
    }
    return { modes, tokens, printers, owner, claims, clients, tenantName };
  }, [pq, target, tenantId, authTenant, session, authOnly]);
  const action = useAction(state.reload);
  const s = state.data;
  const linkedTenant = authOnly ? tenantId || null : (s?.owner?.tenantId ?? null);
  const tenantLabel = authOnly ? (s?.tenantName ?? tenantId) : tenantId;
  const mode = s?.modes.find((m) => m.tenantId.toLowerCase() === tenantId.toLowerCase());
  const missing = missingScopes(s?.claims, SCOPES_PQ);
  const wsToken = s?.tokens.find((t) => t.role === 'WorkServer');
  const wsClient = clientWith(s?.clients, ['printqueue:enqueue']);
  const psToken = s?.tokens.find((t) => t.role === 'PrintServer' && t.name === server);
  const serverPrinters = (s?.printers ?? []).filter((p) => p.printServerName === server);
  const sk = (k: string) => `pq:${target}:${tenantId}:${k}`;
  const psSecret = secrets(sk(`ps-auth:${server}`));
  const psTokenValue = secrets(sk(`ps-token:${server}`));
  const psAuthId = secrets(sk(`ps-auth-id:${server}`));
  const installing = step === 'install';
  useInterval(() => void state.reload(), 15_000, installing && !!server && !serverPrinters.length);

  const steps: WizardStep[] = [
    {
      id: 'tenant',
      title: 'Print queue tenant',
      state: tenantId ? 'done' : 'todo',
      summary: tenantId ? tenantLabel : undefined,
      body: (
        <>
          <Select
            label="Setup"
            value={setup}
            onChange={(v) => {
              setSetup(v);
              setTenantId('');
              setAuthTenant('');
            }}
            options={[
              {
                value: 'auth',
                label: 'AUTH tenant — new installation, AUTH clients only (no tokens, no alias)',
              },
              { value: 'name', label: 'Organization name — existing installation, print queue tokens' },
            ]}
          />
          {authOnly ? (
            <>
              <p>
                The customer's AUTH tenant is the print queue tenant (its id, print queue 1.4.0+). The
                WorkServer and the print servers only need their AUTH clients: no print queue tokens and no
                alias.
              </p>
              <NeedsSession target={target}>
                <TenantPicker
                  target={target}
                  value={tenantId}
                  onChange={(id) => {
                    setTenantId(id);
                    setAuthTenant(id);
                  }}
                  allowNone={false}
                  hint="No tenant yet? Create it with the Customer & tenant wizard."
                />
              </NeedsSession>
            </>
          ) : (
            <>
              <p>
                A print queue tenant is just a name: it exists once a WorkServer or print server uses it. Use
                the WorkServer's <b>organization name</b> — the WorkServer sends it with every job until it
                moves to AUTH tokens.
              </p>
              <TextField
                label="TenantId"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value.trim())}
                list="pq-tenants"
                hint={
                  mode
                    ? `Known to the print queue (mode ${mode.mode}).`
                    : tenantId
                      ? 'New tenant: it appears once something calls with it.'
                      : 'Pick a known one or type a new one.'
                }
              />
              <datalist id="pq-tenants">
                {(s?.modes ?? []).map((m) => (
                  <option key={m.tenantId} value={m.tenantId} />
                ))}
              </datalist>
            </>
          )}
        </>
      ),
    },
    {
      id: 'auth',
      title: authOnly ? 'AUTH scopes' : 'Link to AUTH',
      state: !tenantId ? 'blocked' : linkedTenant && s?.claims && !missing.length ? 'done' : 'todo',
      blocked: 'Pick the tenant first.',
      summary: authOnly
        ? s?.claims && !missing.length
          ? 'printqueue scopes'
          : undefined
        : s?.owner
          ? `${s.owner.tenantName ?? s.owner.tenantId} (${s.owner.customerName})`
          : undefined,
      body: authOnly ? (
        <NeedsSession target={target}>
          <NeedsScope scope={SCOPES.authWrite}>
            <p>
              {tenantLabel} needs the scopes <code>printqueue:enqueue</code> (WorkServer) and{' '}
              <code>printqueue:print</code> (print servers). No alias: its id is its print queue tenant.
            </p>
            <Facts rows={[['Missing scopes', missing.length ? missing.join(', ') : 'none']]} />
            <StepActions error={action.error}>
              {missing.length > 0 && (
                <Button
                  loading={action.busy === 'scopes'}
                  onClick={() => void action.run('scopes', () => addScopes(target, tenantId, missing))}
                >
                  Add {missing.join(', ')}
                </Button>
              )}
            </StepActions>
          </NeedsScope>
        </NeedsSession>
      ) : (
        <NeedsSession target={target}>
          <NeedsScope scope={SCOPES.authWrite}>
            <p>
              The customer's AUTH tenant claims the print queue tenant with the alias{' '}
              <code>printqueue:{tenantId || '<TenantId>'}</code> and needs the scopes{' '}
              <code>printqueue:enqueue</code> (WorkServer) and <code>printqueue:print</code> (print servers).
            </p>
            {linkedTenant ? (
              <Facts
                rows={[
                  ['Linked AUTH tenant', `${s?.owner?.tenantName} (${s?.owner?.customerName})`],
                  ['Missing scopes', missing.length ? missing.join(', ') : 'none'],
                ]}
              />
            ) : (
              <TenantPicker
                target={target}
                value={authTenant}
                onChange={setAuthTenant}
                allowNone={false}
                hint="No tenant yet? Create it with the Customer & tenant wizard."
              />
            )}
            <StepActions error={action.error}>
              {!linkedTenant && (
                <Button
                  disabled={!authTenant || !tenantId}
                  loading={action.busy === 'alias'}
                  onClick={() =>
                    void action.run('alias', async () => {
                      await addAlias(target, 'tenants', authTenant, 'printqueue', tenantId);
                      if (missing.length) await addScopes(target, authTenant, missing);
                    })
                  }
                >
                  Link and add scopes
                </Button>
              )}
              {linkedTenant && missing.length > 0 && (
                <Button
                  loading={action.busy === 'scopes'}
                  onClick={() => void action.run('scopes', () => addScopes(target, linkedTenant, missing))}
                >
                  Add {missing.join(', ')}
                </Button>
              )}
              <Link to="/onboarding/tenant" className="nyk-link">
                Customer & tenant wizard
              </Link>
            </StepActions>
          </NeedsScope>
        </NeedsSession>
      ),
    },
    {
      id: 'workserver',
      title: 'WorkServer',
      state: !tenantId ? 'blocked' : (authOnly ? wsClient : wsToken || wsClient) ? 'done' : 'todo',
      blocked: 'Pick the tenant first.',
      summary:
        [wsClient && `client ${wsClient.name}`, wsToken && 'token'].filter(Boolean).join(' + ') || undefined,
      body: authOnly ? (
        <>
          <p>
            The WorkServer enqueues print jobs with the installation's AUTH client (scope{' '}
            <code>printqueue:enqueue</code>) and no <code>TenantId</code>: the print queue takes the tenant
            from its token. No print queue token.
          </p>
          <Facts
            rows={[
              [
                'AUTH client',
                wsClient
                  ? `${wsClient.name} (${wsClient.clientId})`
                  : 'none with printqueue:enqueue — create it in the Customer & tenant wizard',
              ],
            ]}
          />
          <StepActions>
            <Link to="/onboarding/tenant" className="nyk-link">
              Customer & tenant wizard
            </Link>
          </StepActions>
          {env?.printQueueUri && (
            <FileOutput
              files={workServerFiles({
                authServiceUrl: env.authServiceUrl,
                printQueue: { serviceUrl: `${env.printQueueUri}/`, auth: 'Bearer' },
              })}
            />
          )}
        </>
      ) : (
        <>
          <p>
            The WorkServer enqueues print jobs. With AUTH it uses the installation's client (scope{' '}
            <code>printqueue:enqueue</code>); keep a print queue token as the fallback while the tenant moves
            to AUTH.
          </p>
          <Facts
            rows={[
              [
                'AUTH client',
                wsClient
                  ? `${wsClient.name} (${wsClient.clientId})`
                  : linkedTenant
                    ? 'none with printqueue:enqueue — create it in the Customer & tenant wizard'
                    : 'link the tenant to AUTH first',
              ],
              ['Print queue token', wsToken ? `${wsToken.name ?? 'WorkServer'} ${wsToken.hint}` : 'none'],
            ]}
          />
          <StepActions error={action.error}>
            <Button
              variant={wsToken ? 'ghost' : 'primary'}
              loading={action.busy === 'ws-token'}
              onClick={() =>
                void action.run('ws-token', async () => {
                  const r = await pq.createToken({
                    tenantId,
                    name: 'workserver',
                    role: 'WorkServer',
                    expireDays: 3650,
                  });
                  rememberSecret(sk('ws-token'), r.token);
                })
              }
            >
              {wsToken ? 'Create another WorkServer token' : 'Create WorkServer token'}
            </Button>
          </StepActions>
          {env?.printQueueUri && (
            <FileOutput
              files={workServerFiles({
                authServiceUrl: env.authServiceUrl,
                printQueue: {
                  serviceUrl: `${env.printQueueUri}/`,
                  token: secrets(sk('ws-token')) ?? (wsToken ? PLACEHOLDER : undefined),
                  auth: wsClient ? 'Both' : 'Off',
                },
              })}
            />
          )}
        </>
      ),
    },
    {
      id: 'server',
      title: 'Print server',
      state: !tenantId
        ? 'blocked'
        : server && (psSecret || psAuthId || (!authOnly && psToken))
          ? 'done'
          : 'todo',
      blocked: 'Pick the tenant first.',
      summary: server ? `${server} · ${plat === 'cups' ? 'CUPS' : 'Windows'}` : undefined,
      body: (
        <>
          <div className="nyk-form">
            <TextField
              label="Print server name"
              value={server}
              onChange={(e) => setServer(e.target.value.trim())}
              hint="Unique within the tenant, e.g. the site or machine (PrintServerName)."
            />
            <Select
              label="Runs on"
              value={plat}
              onChange={setPlatform}
              options={[
                { value: 'windows', label: 'Windows (service)' },
                { value: 'cups', label: 'Linux / macOS (CUPS container)' },
              ]}
            />
          </div>
          {authOnly ? (
            <p>
              Give it its own AUTH client (scope <code>printqueue:print</code>): with it the print server
              needs only its name, it gets the tenant from the print queue (print server 2.2.0+).
            </p>
          ) : (
            <p>
              Give it its own AUTH client (scope <code>printqueue:print</code>) and a print queue token as the
              fallback until the tenant requires AUTH. Both go into its config file.
            </p>
          )}
          <Facts
            rows={[
              ['AUTH client', psAuthId ?? (psSecret ? 'issued' : 'none issued in this session')],
              ...(authOnly
                ? []
                : [
                    [
                      'Print queue token',
                      psToken ? `${psToken.hint} (${formatRelative(psToken.dateAdded)})` : 'none',
                    ] as [string, string],
                  ]),
            ]}
          />
          {!linkedTenant && (
            <Note>Link the tenant to AUTH (step 2) to give the print server an AUTH client.</Note>
          )}
          <StepActions error={action.error}>
            <Button
              disabled={!server || !linkedTenant || !session}
              loading={action.busy === 'ps-auth'}
              onClick={() =>
                void action.run('ps-auth', async () => {
                  const r = await pq.printServerClient(tenantId, server);
                  rememberSecret(sk(`ps-auth-id:${server}`), r.authClientId);
                  rememberSecret(sk(`ps-auth:${server}`), r.clientSecret);
                })
              }
            >
              {psSecret ? 'New AUTH secret' : 'Create AUTH client'}
            </Button>
            {!authOnly && (
              <Button
                variant="ghost"
                disabled={!server}
                loading={action.busy === 'ps-token'}
                onClick={() =>
                  void action.run('ps-token', async () => {
                    const r = await pq.createToken({
                      tenantId,
                      name: server,
                      role: 'PrintServer',
                      expireDays: 3650,
                    });
                    rememberSecret(sk(`ps-token:${server}`), r.token);
                  })
                }
              >
                {psToken ? 'Create another token' : 'Create print queue token'}
              </Button>
            )}
          </StepActions>
        </>
      ),
    },
    {
      id: 'install',
      title: 'Install on site',
      state: !server ? 'blocked' : serverPrinters.length ? 'done' : 'waiting',
      blocked: 'Name the print server first.',
      summary: serverPrinters.length ? `${serverPrinters.length} printer(s) reported` : undefined,
      body: env?.printQueueUri ? (
        <>
          <p>
            Give these files and the guide to whoever installs the print server.{' '}
            {plat === 'windows' && config?.printServer.windowsUrl && (
              <>
                Download:{' '}
                <a className="nyk-link" href={config.printServer.windowsUrl}>
                  Windows release zip
                </a>
                .
              </>
            )}{' '}
            <Link
              className="nyk-link"
              to={guidePath(plat === 'windows' ? 'printserver-windows' : 'printserver-cups', {
                printServerName: server,
                tenantId: tenantLabel,
                printQueueUri: env.printQueueUri,
                windowsUrl: config?.printServer.windowsUrl,
                cupsImage: config?.printServer.cupsImage,
              })}
            >
              Installation guide
            </Link>
          </p>
          <FileOutput
            zipName={`printserver-${server}.zip`}
            files={printServerFiles(
              {
                printQueueUri: env.printQueueUri,
                printServerName: server,
                // AUTH only: the print server asks the print queue for its tenant
                tenantId: authOnly ? undefined : tenantId,
                token: authOnly ? undefined : (psTokenValue ?? (psToken ? PLACEHOLDER : undefined)),
                auth:
                  psAuthId || psSecret
                    ? { clientId: psAuthId ?? PLACEHOLDER, clientSecret: psSecret ?? PLACEHOLDER }
                    : undefined,
              },
              plat,
              config?.printServer.cupsImage ?? 'images.nycelogic.com/nyce.logic/printserver-cups:latest'
            )}
          />
          {serverPrinters.length ? (
            <Note tone="good">
              {server} reported {serverPrinters.map((p) => p.name).join(', ')}.
            </Note>
          ) : (
            <Note tone="warn">
              Waiting for {server} to report its printers (checked every 15 s; up to 10 minutes after start).
            </Note>
          )}
          <StepActions>
            <Button
              variant="ghost"
              onClick={() => {
                setServer('');
                setStep('server');
              }}
            >
              Add another print server
            </Button>
          </StepActions>
        </>
      ) : (
        <Note tone="warn">This environment has no print queue URL configured.</Note>
      ),
    },
    {
      id: 'auth-mode',
      title: 'Move to AUTH',
      state: !mode ? 'blocked' : mode.mode === 'Auth' ? 'done' : 'optional',
      blocked: 'The tenant appears here once its WorkServer or a print server has called.',
      summary: mode ? `mode ${mode.mode}` : undefined,
      body: mode ? (
        <>
          <Facts
            rows={[
              ['Mode', mode.mode],
              ['Last print queue token', mode.lastLegacyAt ? formatRelative(mode.lastLegacyAt) : '—'],
              ['Last AUTH token', mode.lastBearerAt ? formatRelative(mode.lastBearerAt) : '—'],
            ]}
          />
          <p>
            <b>Both</b> accepts AUTH tokens next to the print queue tokens. <b>Auth</b> refuses the tenant's
            print queue tokens: only once the WorkServer and every print server show up with AUTH tokens.
          </p>
          <StepActions error={action.error}>
            {mode.mode === 'Legacy' && (
              <Button onClick={() => void action.run('mode', () => pq.setTenantAuthMode(tenantId, 'Both'))}>
                Allow AUTH (Both)
              </Button>
            )}
            {mode.mode !== 'Auth' && mode.lastBearerAt && (
              <Button
                variant="ghost"
                onClick={() => void action.run('mode', () => pq.setTenantAuthMode(tenantId, 'Auth'))}
              >
                Require AUTH
              </Button>
            )}
          </StepActions>
        </>
      ) : null,
    },
  ];

  return (
    <Wizard
      // AUTH only has no print queue tokens to move away from
      steps={authOnly ? steps.filter((x) => x.id !== 'auth-mode') : steps}
      loading={state.loading && !s}
      error={state.error}
      onRecheck={() => void state.reload()}
    />
  );
}
